"use client";
import ContactForm from "@/components/InquryForm/ContactForm";
import { useTranslations } from "next-intl";
import Image from "next/image";

const UploadPage = ({ locale }: { locale: string }) => {
	const t = useTranslations("upload");

	return (
		<div className="min-h-screen bg-themeSecondary50">
			<div className="container mx-auto max-w-full px-4 py-12">
				<div className="flex flex-col gap-12 lg:flex-row lg:gap-16">
					{/* 左侧内容区域 */}
					<div className="flex w-full flex-col items-center justify-around lg:w-1/2 lg:items-stretch">
						{/* 主标题区域 */}
						<div className="text-center lg:text-left">
							<h1 className="mb-6 text-4xl font-bold leading-tight text-themeSecondary900 lg:text-5xl">
								{t("title")}
							</h1>
							<p className="text-lg text-themeSecondary600">{t("subtitle")}</p>
						</div>

						{/* 功能特性列表 */}
						<div className="space-y-4">
							<h2 className="mb-4 text-xl font-semibold text-themeSecondary800">{t("whyChooseTitle")}</h2>
							<div className="grid gap-4 sm:grid-cols-2">
								{[
									t("feature1"),
									t("feature2"),
									t("feature3"),
									t("feature4"),
									t("feature5"),
									t("feature6"),
									t("feature7"),
									t("feature8"),
								].map((feature, index) => (
									<div key={index} className="flex items-start space-x-3">
										<div className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-main"></div>
										<span className="leading-relaxed text-themeSecondary700">{feature}</span>
									</div>
								))}
							</div>
						</div>

						{/* 商标 + Trustpilot：定位元素强制水平对齐 */}
						<div className="relative w-full py-4" style={{ height: "56px" }}>
							{/* 商标：绝对定位左对齐，垂直居中 */}
							<div
								className="absolute left-0 top-1/2 -translate-y-1/2 overflow-hidden"
								style={{ width: "200px", height: "48px" }}
							>
								<div
									className="absolute inset-0 flex items-center justify-center"
									style={{ transform: "scale(1.4)" }}
								>
									<Image
										src="/image/upload.png"
										alt="ISO 9001, IATF 16949, ISO 13485, CE certification"
										width={400}
										height={80}
										className="h-full w-auto object-contain"
										sizes="280px"
										priority={false}
									/>
								</div>
							</div>
							{/* Trustpilot：移动端隐藏，sm+ 显示并绝对定位 */}
							<div
								className="trustpilot-widget absolute left-[224px] right-0 top-[75px] hidden -translate-y-1/2 sm:left-[236px] sm:block"
								data-locale="en-US"
								data-template-id="5419b732fbfb950b10de65e5"
								data-businessunit-id="68808142dd0a4c4d3e411b9c"
							>
								<iframe
									title="Customer reviews powered by Trustpilot"
									loading="lazy"
									src="https://widget.trustpilot.com/trustboxes/5419b732fbfb950b10de65e5/index.html?templateId=5419b732fbfb950b10de65e5&businessunitId=68808142dd0a4c4d3e411b9c#locale=en-US&styleHeight=24px&styleWidth=100%25"
									style={{
										height: "24px",
										width: "100%",
										border: "none",
										display: "block",
									}}
								/>
							</div>
						</div>

						{/* 信任标识 */}
						<div className="rounded-xl border border-themeWarning200 bg-themeWarning50 p-6 shadow-sm">
							<div className="flex flex-col items-center space-y-3 sm:flex-row sm:space-x-4 sm:space-y-0">
								<div className="flex text-themeWarning500">
									{Array.from({ length: 5 }).map((_, i) => (
										<svg key={i} className="h-6 w-6 fill-current" viewBox="0 0 20 20">
											<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
										</svg>
									))}
								</div>
								<span className="text-center font-semibold text-themeSecondary800 sm:text-left">
									{t("trustedBy")}
								</span>
							</div>
						</div>

						{/* 合作伙伴信息 */}
						<div className="rounded-xl border border-themeSecondary200 bg-white p-6 shadow-sm">
							<div className="flex items-start space-x-4">
								<div className="flex h-12 w-12 items-center justify-center rounded-lg bg-themeSecondary200 shadow-sm">
									<Image src="/image/logo1.png" alt="logo" width={100} height={48} />
								</div>
								<div className="flex-1">
									<h3 className="mb-2 font-semibold text-themeSecondary800">{t("partnershipTitle")}</h3>
									<p className="leading-relaxed text-themeSecondary600">{t("partnershipDesc")}</p>
								</div>
							</div>
						</div>
					</div>

					{/* 右侧表单区域 */}
					<div className="w-full min-w-0 lg:w-1/2">
						<ContactForm locale={locale} className="upload-contact-form !py-0" uploadRequired />
					</div>
				</div>
			</div>
		</div>
	);
};

export default UploadPage;
